import { ageInYears } from '../../../utils/age';

// The Patient Register's filters, kept apart from the screen so the rules can be tested on their
// own. Every filter works on the rows already loaded: the register is the whole patient base in
// one response, and narrowing it here is instant, where asking the server again for every click
// would not be.

/** Age bands for the filter. `max` is inclusive; a patient with no date of birth is 'unknown'. */
export const AGE_BANDS = [
  { value: 'under1', label: 'Under 1 year', min: 0, max: 0 },
  { value: '1-4', label: '1 to 4 years', min: 1, max: 4 },
  { value: '5-14', label: '5 to 14 years', min: 5, max: 14 },
  { value: '15-17', label: '15 to 17 years', min: 15, max: 17 },
  { value: 'minor', label: 'All under 18', min: 0, max: 17 },
  { value: '18-39', label: '18 to 39 years', min: 18, max: 39 },
  { value: '40-59', label: '40 to 59 years', min: 40, max: 59 },
  { value: '60+', label: '60 and over', min: 60, max: Infinity },
  { value: 'unknown', label: 'Age not recorded' },
];

export const VISIT_FILTERS = [
  { value: 'never', label: 'Never visited' },
  { value: 'last30', label: 'Seen in the last 30 days' },
  { value: 'last90', label: 'Seen in the last 90 days' },
  { value: 'last365', label: 'Seen in the last 12 months' },
  { value: 'lapsed180', label: 'Not seen for over 6 months' },
  { value: 'once', label: 'Visited once' },
  { value: 'repeat5', label: 'Visited 5 or more times' },
];

export const FAMILY_FILTERS = [
  { value: 'head', label: 'Family heads' },
  { value: 'member', label: 'Family members' },
  { value: 'none', label: 'Not in a family' },
  { value: 'guardian', label: 'Guardian recorded' },
];

export const ID_FILTERS = [
  { value: 'own', label: 'Own CNIC recorded' },
  { value: 'relative', label: "Only a relative's CNIC" },
  { value: 'bform', label: 'B-Form recorded' },
  { value: 'none', label: 'No CNIC at all' },
];

export const CLINICAL_FILTERS = [
  { value: 'alerts', label: 'Has active medical alerts' },
  { value: 'critical', label: 'Has a critical or high alert' },
  { value: 'history', label: 'Medical history taken' },
  { value: 'noHistory', label: 'No medical history yet' },
];

export const MONEY_FILTERS = [
  { value: 'unpaid', label: 'Bills awaiting payment' },
  { value: 'credit', label: 'Holds credit' },
  { value: 'paid', label: 'Has paid the hospital' },
  { value: 'neverPaid', label: 'Has never paid' },
];

export const REGISTERED_FILTERS = [
  { value: 'thisMonth', label: 'This month' },
  { value: 'last3Months', label: 'Last 3 months' },
  { value: 'thisYear', label: 'This year' },
  { value: 'custom', label: 'Custom dates' },
];

export const EMPTY_FILTERS = {
  q: '',
  gender: '',
  age: '',
  status: '',
  place: '',
  visits: '',
  family: '',
  id: '',
  clinical: '',
  money: '',
  registered: '',
  registeredFrom: '',
  registeredTo: '',
};

/** How many filters are set, for the "Clear filters" button. A custom range counts once. */
export const activeFilterCount = (f) =>
  Object.keys(EMPTY_FILTERS).filter((k) => k !== 'registeredFrom' && k !== 'registeredTo' && f[k]).length;

const digits = (s) => String(s || '').replace(/\D/g, '');

/** Where the patient lives, the most specific part first, as one line. */
export const placeOf = (r) => [r.area, r.town, r.city].filter(Boolean).join(', ');

/** Whole days from an ISO date to today's ISO date, both on the clinic calendar. */
export const daysBetween = (fromIso, toIso) => {
  if (!fromIso || !toIso) return null;
  const [y1, m1, d1] = fromIso.slice(0, 10).split('-').map(Number);
  const [y2, m2, d2] = toIso.slice(0, 10).split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
};

const shiftMonths = (todayIso, months) => {
  const [y, m, d] = todayIso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1 - months, 1));
  if (months === 0) return `${y}-${String(m).padStart(2, '0')}-01`;
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(Math.min(d, 28)).padStart(2, '0')}`;
};

/** The registered-on window a chip stands for, as [from, to] ISO dates (either may be empty). */
export const registeredWindow = (f, todayIso) => {
  switch (f.registered) {
    case 'thisMonth':
      return [shiftMonths(todayIso, 0), todayIso];
    case 'last3Months':
      return [shiftMonths(todayIso, 3), todayIso];
    case 'thisYear':
      return [`${todayIso.slice(0, 4)}-01-01`, todayIso];
    case 'custom':
      return [f.registeredFrom || '', f.registeredTo || ''];
    default:
      return ['', ''];
  }
};

/**
 * The search box: any word of the name, an MRN, a phone number (on its digits, so "0300 123" finds
 * "+92-300-123..."), a CNIC or B-Form (on its digits), a relative's name, or a place. Every word
 * typed has to match somewhere, so "ayesha chak" narrows to Ayesha from a Chak.
 */
export const matchesSearch = (r, q) => {
  const words = String(q || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = [
    r.name, r.mrn, r.fatherName, r.motherName, r.husbandName, r.guardianName,
    r.area, r.town, r.city, r.familyHeadName, r.familyHeadMrn,
  ]
    .filter(Boolean)
    .join(' | ')
    .toLowerCase();
  const numbers = [r.phone, r.guardianPhone, r.cnic, r.idCnic, r.bForm, r.guardianCnic].map(digits).join('|');
  return words.every((w) => {
    if (text.includes(w)) return true;
    const d = digits(w);
    // A phone number is matched on its last ten digits, so a leading 0 typed at the counter
    // still finds a number stored with +92.
    const probe = d.length > 10 ? d.slice(-10) : d.replace(/^0(?=\d{9,})/, '');
    return d.length >= 3 && numbers.includes(probe);
  });
};

/** One patient against every filter. `todayIso` is the clinic's today, passed in for testing. */
export const matchesFilters = (r, f, todayIso) => {
  if (!matchesSearch(r, f.q)) return false;
  if (f.gender && r.gender !== f.gender) return false;
  if (f.status && r.status !== f.status) return false;
  if (f.place && placeOf(r) !== f.place) return false;

  if (f.age) {
    const age = ageInYears(r.dateOfBirth);
    if (f.age === 'unknown') {
      if (age !== null) return false;
    } else {
      const band = AGE_BANDS.find((b) => b.value === f.age);
      if (age === null || !band || age < band.min || age > band.max) return false;
    }
  }

  if (f.registered) {
    const [from, to] = registeredWindow(f, todayIso);
    const day = (r.registeredAt || '').slice(0, 10);
    if (!day) return false;
    if (from && day < from) return false;
    if (to && day > to) return false;
  }

  if (f.visits) {
    const visits = Number(r.visits) || 0;
    const since = r.lastVisit ? daysBetween(r.lastVisit, todayIso) : null;
    switch (f.visits) {
      case 'never': if (visits !== 0) return false; break;
      case 'last30': if (since === null || since > 30) return false; break;
      case 'last90': if (since === null || since > 90) return false; break;
      case 'last365': if (since === null || since > 365) return false; break;
      case 'lapsed180': if (since === null || since <= 180) return false; break;
      case 'once': if (visits !== 1) return false; break;
      case 'repeat5': if (visits < 5) return false; break;
      default: break;
    }
  }

  if (f.family) {
    if (f.family === 'head' && r.familyRole !== 'HEAD') return false;
    if (f.family === 'member' && r.familyRole !== 'MEMBER') return false;
    if (f.family === 'none' && r.familyRole) return false;
    if (f.family === 'guardian' && !r.guardianName) return false;
  }

  if (f.id) {
    if (f.id === 'own' && !r.cnic) return false;
    if (f.id === 'relative' && (r.cnic || !r.idCnic)) return false;
    if (f.id === 'bform' && !r.bForm) return false;
    if (f.id === 'none' && r.idCnic) return false;
  }

  if (f.clinical) {
    if (f.clinical === 'alerts' && !(Number(r.activeAlerts) > 0)) return false;
    if (f.clinical === 'critical' && !['CRITICAL', 'HIGH'].includes(r.worstAlert)) return false;
    if (f.clinical === 'history' && !r.medicalHistory) return false;
    if (f.clinical === 'noHistory' && r.medicalHistory) return false;
  }

  if (f.money) {
    if (f.money === 'unpaid' && !(Number(r.unpaidBills) > 0)) return false;
    if (f.money === 'credit' && !(Number(r.creditBalance) > 0)) return false;
    if (f.money === 'paid' && !(Number(r.totalPaid) > 0)) return false;
    if (f.money === 'neverPaid' && Number(r.totalPaid) > 0) return false;
  }
  return true;
};

/** A plain-words list of the filters set, for the printout and the CSV's reader. */
export const describeFilters = (f, places = {}) => {
  const label = (list, v) => (list.find((x) => x.value === v) || {}).label || v;
  const parts = [];
  if (f.q) parts.push(`search "${f.q}"`);
  if (f.gender) parts.push(f.gender.charAt(0) + f.gender.slice(1).toLowerCase());
  if (f.age) parts.push(label(AGE_BANDS, f.age));
  if (f.status) parts.push(`status ${f.status.charAt(0) + f.status.slice(1).toLowerCase()}`);
  if (f.place) parts.push(places[f.place] || f.place);
  if (f.registered) {
    parts.push(
      f.registered === 'custom'
        ? `registered ${f.registeredFrom || 'any time'} to ${f.registeredTo || 'today'}`
        : `registered ${label(REGISTERED_FILTERS, f.registered).toLowerCase()}`
    );
  }
  if (f.visits) parts.push(label(VISIT_FILTERS, f.visits).toLowerCase());
  if (f.family) parts.push(label(FAMILY_FILTERS, f.family).toLowerCase());
  if (f.id) parts.push(label(ID_FILTERS, f.id).toLowerCase());
  if (f.clinical) parts.push(label(CLINICAL_FILTERS, f.clinical).toLowerCase());
  if (f.money) parts.push(label(MONEY_FILTERS, f.money).toLowerCase());
  return parts.length ? parts.join('; ') : 'none, every patient';
};
